// Angular imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

// RxJs imports
import { Subscription } from 'rxjs';

// 3rd party imports
import notify from 'devextreme/ui/notify';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { FileReaderService } from '../../_services/shared-services/file-reader.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { CommunityService } from '../../community-module/_services/data-services/community.service';

// Model imports
import { AttachmentParameterDto } from '../../_models/attachment-parameter-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { OnlineRequestCategoryResultDto } from '../../_models/online-request-category-result-dto';
import { ResultStatusDto } from '../../_models/result-status-dto';
import { SubmitRequestParameterDto } from '../../_models/submit-request-parameter-dto';
import { UserPropertyResultDto } from '../../_models/user-property-result-dto';

// Constant imports
import { ContactConstants } from '../../_constants/contact-constants';
import { MessageInterval } from '../../_constants/messages-intervals';

// CC-Common imports
import {
  CCMessageBoxIcon,
  IconConstants,
  MessageBoxComponent,
  PrintableCharsPipe
} from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss'],
  providers: [PrintableCharsPipe],
  standalone: false
})
export class ContactUsComponent implements OnInit, OnDestroy {
  @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;
  isSmallScreen = false;
  loadingVisible = false;
  selectedProperty: UserPropertyResultDto;
  propertyChangeSubscription: Subscription;
  issueCategories: OnlineRequestCategoryResultDto[];

  helpLink: string;
  contactUsPhone: string = ContactConstants.contactUsPhone;
  contactUsPhoneDisplayText: string =
    ContactConstants.contactUsPhoneDisplayText;
  selectedCategoryId = -1;
  descriptionValue = '';
  descriptionLengthLeft = 300;
  selectedAttachments: File[] = [];

  saveIcon = IconConstants.UPLOAD;
  descriptionStatus = 'valid';

  constructor(
    private screenService: ScreenService,
    private fileService: FileReaderService,
    private userService: UserService,
    private ciraHelpService: CiraHelpService,
    private communityService: CommunityService,
    private errorService: ServerErrorService
  ) {}

  ngOnInit(): void {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    this.populateIssueCategories();
    this.propertyChangeSubscription = RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      () => {
        this.populateIssueCategories();
      }
    );

    this.updateContactPhoneNo();
    RxjsUtils.subscribeToSubject(
      this.userService.getSubjectBrandingInforamtionChanged(),
      () => {
        this.updateContactPhoneNo();
      }
    );
  }

  ngOnDestroy(): void {
    if (!!this.propertyChangeSubscription) {
      this.propertyChangeSubscription.unsubscribe();
    }
  }

  getScreenByWidth = (width: number): string => {
    return this.isSmallScreen ? 'sm' : 'lg';
  };

  descriptionChanged(): void {
    this.descriptionLengthLeft = 300 - this.descriptionValue.length;
  }

  submitOnlineRequest() {
    if (this.validateInput()) {
      this.loadingVisible = true;
      if (this.selectedAttachments.length > 0) {
        RxjsUtils.safeSubscribe(
          this.fileService.readFiles(this.selectedAttachments),
          (attachments: AttachmentParameterDto[]) => {
            this.submitRequest(attachments);
          }
        );
      } else {
        this.submitRequest([]);
      }
    }
  }

  private submitRequest(attachments: AttachmentParameterDto[]) {
    const reqParams: SubmitRequestParameterDto =
      new SubmitRequestParameterDto();
    reqParams.communityId = this.selectedProperty.communityId;
    reqParams.customerId = this.selectedProperty.customerId;
    reqParams.categoryId = this.selectedCategoryId;
    reqParams.notes = this.descriptionValue;
    reqParams.requestAttachments = attachments;
    RxjsUtils.safeSubscribe(
      this.communityService.submitContactUsRequest(reqParams),
      (result: ResultStatusDto) => {
        this.loadingVisible = false;
        if (result.isSuccess) {
          notify(
            'Your request has been submitted successfully.',
            'success',
            MessageInterval.successMsgInterval
          );
          this.clearAllFields();
        } else {
          this.messageBox.showWithFixedWidthAndIcon(
            CCMessageBoxIcon.error,
            'Error',
            `ERROR - ${result.message}`,
            320
          );
        }
      },
      (error) => {
        this.errorService.displayError(error);
        this.loadingVisible = false;
      }
    );
  }

  private populateIssueCategories(): void {
    this.selectedProperty = this.userService.getSelectedProperty();
    this.issueCategories = [];
    this.selectedCategoryId = -1;
    if (!!this.selectedProperty) {
      this.loadingVisible = true;
      const baseParams = new BaseResidentParameterDto(
        this.selectedProperty.customerId,
        this.selectedProperty.communityId
      );
      RxjsUtils.safeSubscribe(
        this.communityService.getContactUsIssueCategories(baseParams),
        (results: OnlineRequestCategoryResultDto[]) => {
          this.loadingVisible = false;
          this.issueCategories = results;
          this.selectedCategoryId =
            results && results.length > 0 ? results[0].categoryId : -1;
        },
        (error) => {
          this.errorService.displayError(error);
          this.loadingVisible = false;
        }
      );
    }
    this.updateHelpLink();
  }

  private clearAllFields(): void {
    this.descriptionValue = '';
    this.selectedAttachments = [];
    this.descriptionChanged();
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  private updateHelpLink(): void {
    if (!!this.selectedProperty && !!this.selectedProperty.communityId) {
      this.helpLink = this.ciraHelpService.getContactUsHelpLink(
        this.selectedProperty.communityId
      );
    } else {
      this.helpLink = this.ciraHelpService.getContactUsHelpLink(null);
    }
  }

  private validateInput(): boolean {
    const isValid = this.descriptionValue.trim().length > 0;
    this.descriptionStatus = 'valid';
    if (!isValid) {
      this.descriptionStatus = 'invalid';
    }
    return isValid;
  }

  private updateContactPhoneNo() {
    this.contactUsPhone = ContactConstants.contactUsPhone;
    this.contactUsPhoneDisplayText = ContactConstants.contactUsPhoneDisplayText;

    const brandingInfo = this.userService.getBrandingInforamtion();
    if (!!brandingInfo) {
      this.contactUsPhone =
        brandingInfo.tollFreePhone ||
        brandingInfo.phone ||
        ContactConstants.contactUsPhone;
      this.contactUsPhoneDisplayText =
        brandingInfo.tollFreePhone ||
        brandingInfo.phone ||
        ContactConstants.contactUsPhoneDisplayText;
    }
  }
}
